export type WebsiteDescriptionRow = { label: string; values: string[] };

export type WebsiteDescription = {
  summary: string;
  tooltipRows: WebsiteDescriptionRow[];
};

const formatMoney = (amount?: number | string, currency?: string) => {
  if (amount === undefined || amount === null || amount === '') return '';
  const num = Number(amount);
  const formatted = Number.isFinite(num) ? num.toLocaleString('en-IN') : String(amount);
  return `${currency || 'INR'} ${formatted}`.trim();
};

const getJsonFromDescription = (description: string) => {
  const marker = 'bookingDetails=';
  const idx = description.indexOf(marker);
  if (idx === -1) return null;
  const tail = description.slice(idx + marker.length);
  const firstBrace = tail.indexOf('{');
  const lastBrace = tail.lastIndexOf('}');
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) return null;
  const jsonText = tail.slice(firstBrace, lastBrace + 1);
  try {
    return JSON.parse(jsonText);
  } catch {
    return null;
  }
};

const valueAfter = (text: string, label: string, nextLabels: string[]) => {
  const next = nextLabels.map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const match = text.match(new RegExp(`${label}\\s*:\\s*(.+?)(?=\\s+(?:${next})\\s*:|$)`, 'i'));
  return match?.[1]?.trim() || '';
};

const requestSummary = (header: string) => {
  const match = header.match(/^(?:Notes:\s*)?(.+?)(?=\s+Package\s*:|$)/i);
  return match?.[1]?.trim() || '';
};

export const buildWebsiteDescription = (description: string): WebsiteDescription | null => {
  const marker = 'bookingDetails=';
  const idx = description.indexOf(marker);
  const header = idx !== -1 ? description.slice(0, idx).trim() : '';
  const data = getJsonFromDescription(description);
  if (!data) return null;

  const pkgName = data?.packageName || data?.packageId;
  const pkgId = data?.packageId && data?.packageName ? ` (ID: ${data.packageId})` : '';

  const passengers = data?.passengers || {};
  const adults = passengers?.adults;
  const children = passengers?.children;
  const total = passengers?.total;
  const rooms = Array.isArray(passengers?.rooms)
    ? passengers.rooms.length
    : Number(passengers?.rooms) || 0;
  const paxParts = [
    adults !== undefined ? `${adults} adults` : '',
    children !== undefined ? `${children} children` : '',
    total !== undefined ? `total ${total}` : '',
    rooms ? `${rooms} rooms` : '',
  ].filter(Boolean);
  const paxText = paxParts.length ? paxParts.join(', ') : '';
  const summaryParts = [
    pkgName ? `Package: ${pkgName}${pkgId}` : '',
    paxText ? `Pax: ${paxText}` : '',
  ].filter(Boolean);
  const summary = summaryParts.length ? summaryParts.join(' - ') : 'Package enquiry';

  const tooltipRows: WebsiteDescriptionRow[] = [];
  const enquiry = requestSummary(header);
  if (enquiry) tooltipRows.push({ label: 'Request', values: [enquiry] });
  if (pkgName) tooltipRows.push({ label: 'Package', values: [`${pkgName}${pkgId}`] });
  if (paxText) tooltipRows.push({ label: 'Pax', values: [paxText] });

  const headerLabels = ['Package', 'Travel Date', 'Travelers', 'Selected hotels', 'Flight', 'Selected cab', 'Estimated package total'];
  const travelDate = valueAfter(header, 'Travel Date', headerLabels);
  if (travelDate) tooltipRows.push({ label: 'Travel date', values: [travelDate] });

  const hotels = data?.hotels?.selected?.length
    ? data.hotels.selected
    : data?.hotels?.base || [];
  if (Array.isArray(hotels) && hotels.length) {
    const hotelLines: string[] = [];
    hotels.slice(0, 3).forEach((hotel: any) => {
      const dates = hotel.checkInDate && hotel.checkOutDate
        ? `${hotel.checkInDate} to ${hotel.checkOutDate}`
        : '';
      const price = formatMoney(hotel.price, hotel.currency);
      const parts = [
        hotel.name || 'Hotel',
        hotel.city,
        hotel.nights,
        hotel.roomType,
        dates,
        hotel.boardBasis,
        price,
      ].filter(Boolean);
      if (parts.length) hotelLines.push(parts.join(' | '));
    });
    if (hotels.length > 3) {
      hotelLines.push(`Hotels: +${hotels.length - 3} more`);
    }
    if (hotelLines.length) tooltipRows.push({ label: 'Hotels', values: hotelLines });
  }

  const outbound = data?.flights?.outbound;
  if (outbound) {
    const price = formatMoney(outbound.price, outbound.currency);
    const parts = [
      `Outbound: ${outbound.airline || outbound.provider || ''}`.trim(),
      outbound.fromCode && outbound.toCode ? `${outbound.fromCode} to ${outbound.toCode}` : '',
      outbound.departureTime && outbound.arrivalTime ? `${outbound.departureTime} to ${outbound.arrivalTime}` : '',
      outbound.duration ? `Duration: ${outbound.duration}` : '',
      outbound.stops !== undefined ? `Stops: ${outbound.stops}` : '',
      price ? `Price: ${price}` : '',
    ].filter(Boolean);
    if (parts.length) tooltipRows.push({ label: 'Outbound', values: [parts.join(' | ')] });
  }
  const inbound = data?.flights?.return;
  if (inbound) {
    const price = formatMoney(inbound.price, inbound.currency);
    const parts = [
      `Return: ${inbound.airline || inbound.provider || ''}`.trim(),
      inbound.fromCode && inbound.toCode ? `${inbound.fromCode} to ${inbound.toCode}` : '',
      inbound.departureTime && inbound.arrivalTime ? `${inbound.departureTime} to ${inbound.arrivalTime}` : '',
      inbound.duration ? `Duration: ${inbound.duration}` : '',
      inbound.stops !== undefined ? `Stops: ${inbound.stops}` : '',
      price ? `Price: ${price}` : '',
    ].filter(Boolean);
    if (parts.length) tooltipRows.push({ label: 'Return', values: [parts.join(' | ')] });
  }

  const selectedCab = valueAfter(header, 'Selected cab', headerLabels);
  const flightSelection = valueAfter(header, 'Flight', headerLabels);
  const transport = [
    flightSelection ? `Flight: ${flightSelection}` : '',
    selectedCab ? `Cab: ${selectedCab}` : '',
  ].filter(Boolean);
  if (transport.length) tooltipRows.push({ label: 'Transport', values: [transport.join(' · ')] });

  const estimatedTotal = valueAfter(header, 'Estimated package total', headerLabels);
  if (estimatedTotal) tooltipRows.push({ label: 'Estimated total', values: [estimatedTotal] });

  const inclusions = data?.inclusions?.status || {};
  const enabled = Object.entries(inclusions)
    .filter(([, value]) => value)
    .map(([key]) => key);
  if (enabled.length) {
    tooltipRows.push({ label: 'Includes', values: [enabled.join(', ')] });
  }

  const customVisa = data?.inclusions?.customVisa;
  if (customVisa) {
    const visaParts = [
      customVisa.type ? `Type: ${customVisa.type}` : '',
      customVisa.validity ? `Validity: ${customVisa.validity}` : '',
      customVisa.timeRequired ? `Time: ${customVisa.timeRequired}` : '',
      customVisa.price ? `Price: ${formatMoney(customVisa.price, customVisa.currency)}` : '',
    ].filter(Boolean);
    if (visaParts.length) tooltipRows.push({ label: 'Visa', values: [visaParts.join(' | ')] });
  }

  const customInsurance = data?.inclusions?.customInsurance;
  if (customInsurance) {
    const insuranceParts = [
      customInsurance.name ? `Name: ${customInsurance.name}` : '',
      customInsurance.description ? customInsurance.description : '',
      customInsurance.price ? `Price: ${formatMoney(customInsurance.price, customInsurance.currency)}` : '',
    ].filter(Boolean);
    if (insuranceParts.length) tooltipRows.push({ label: 'Insurance', values: [insuranceParts.join(' | ')] });
  }

  return { summary, tooltipRows };
};
